/**
 * Utility functions for interacting with social elements on the site.
 */
import {t} from "@lingui/core/macro";

import {loadFacebookApi} from "@khan/auth-facebook";

const Social = {
    /* Any view that calls facebookVideo should try to call this
     function before, like on a mouseover event, so that FB
     is loaded before they actually try to share. Otherwise,
     users will see a pop-up blocker.
    */
    prepFacebook: function (): undefined {
        loadFacebookApi();
    },

    facebookShare: function (
        name: string,
        url: string,
        image?: string | null,
    ): boolean {
        loadFacebookApi().then((FB) => {
            FB.ui({
                method: "feed",
                name: name,
                link: url,
                picture: image,
                caption: "www.khanacademy.org",
            });
        });
        return false;
    },

    /** Publish a standard "post" action to user's Facebook Timeline */
    facebookVideo: function (name: string, desc: string, url: string): boolean {
        loadFacebookApi().then((FB) => {
            FB.ui({
                method: "feed",
                name: name,
                link: "http://www.khanacademy.org/" + url,
                picture:
                    "http://www.khanacademy.org/images/handtreehorizontal_facebook.png",
                caption: "www.khanacademy.org",
                description: desc,
                message: t`I just learned about ${name} on Khan Academy`,
            });
        });
        return false;
    },

    /**
     * Format a mailto: url for sending an email with the provided subject and
     * body.
     *
     * Example usage:
     *
     *      var emailUrl = Social.formatMailtoUrl({
     *          subject: "Check out all this awesome on Khan Academy!",
     *          body: "Check it out at http://www.khanacademy.org."
     *      });
     */
    formatMailtoUrl: function (options: {
        subject: string;
        body: string;
    }): string {
        const subject = options.subject;
        const body = options.body;

        const href =
            "mailto:" +
            "?Subject=" +
            encodeURIComponent(subject) +
            "&Body=" +
            encodeURIComponent(body);

        return href.replace(/\s/g, "+");
    },

    emailBadge: function (url: string, desc: string): string {
        return Social.formatMailtoUrl({
            subject: t`I just earned the ${desc} badge on Khan Academy!`,
            body: t`Check it out at ${url}.`,
        });
    },

    /**
     * Open a popup window centered in the screen with the specified url.
     * To be used for opening a popup for tweeting.
     */
    openTwitterPopup: function (url: string): undefined {
        const width = 550;
        const height = 370;
        const winWidth = document.documentElement?.clientWidth ?? 0;
        const winHeight = document.documentElement?.clientHeight ?? 0;
        const left = (winWidth - width) / 2;
        const top = (winHeight - height) / 2;
        const opts =
            "status=1" +
            ",width=" +
            width +
            ",height=" +
            height +
            ",top=" +
            top +
            ",left=" +
            left;
        window.open(url, "twitter", opts);
    },

    /**
     * Format a twitter URL for sharing the specified url with the specified
     * text.
     *
     * Example usage:
     *
     *      var twitterUrl = Social.formatTwitterShareUrl({
     *          url: "http://www.khanacademy.org/computer-programming",
     *          text: "Check out these explorations on Khan Academy!"
     *      });
     */
    formatTwitterShareUrl: function (options: {
        url: string;
        text: string;
    }): string {
        const url = options.url;
        const text = options.text;

        const href =
            "http://twitter.com/intent/" +
            "tweet?url=" +
            encodeURIComponent(url) +
            "&text=" +
            encodeURIComponent(text) +
            "&related=khanacademy:Khan Academy";

        return href.replace(/\s/g, "+");
    },

    twitterBadge: function (url: string, desc: string): string {
        return Social.formatTwitterShareUrl({
            url: url,
            text: t`I just earned the ${desc} badge on @khanacademy! Check it out at`,
        });
    },
} as const;

export default Social;
